import { reactive, computed } from "vue";
import {
  getPayPlanVersionDetailV2,
  voidPayPlanVersionV2,
} from "@/api/employeeAPI";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { apiErrorMessage } from "@components/ProposedPayPlanDrawer/composables/useProposalDrawer";

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

/** Normalize the detail endpoint's raw enum status to the drawer's token. */
export function normalizeVersionStatus(status) {
  switch (String(status || "").toUpperCase()) {
    case "IN_FORCE":
      return "in-force";
    case "TERMINATED":
      return "terminated";
    case "PROPOSED":
      return "proposed";
    case "VOIDED":
      return "voided";
    default:
      return "in-force";
  }
}

/**
 * Classify why a committed version lapsed, from the detail payload.
 * - TERMINATED status -> terminated (no void)
 * - a newer non-voided version exists (void_blocked_by) -> superseded
 * - otherwise it is the plan's newest non-voided, closed fixed term -> fixed-term-ended
 */
export function resolveLapsedReason(detail) {
  if (String(detail?.status || "").toUpperCase() === "TERMINATED") {
    return "terminated";
  }
  if (detail?.void_blocked_by) {
    return "superseded";
  }
  return "fixed-term-ended";
}

const CURRENCY_SYMBOLS = { USD: "$", GBP: "£", EUR: "€", JPY: "¥" };

export function currencySymbolFor(code) {
  if (!code) return "$";
  return CURRENCY_SYMBOLS[String(code).toUpperCase()] || `${code} `;
}

function toNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const FAIR_PAY_STATUS_LABELS = {
  "fairly-paid": "Fairly paid",
  below: "Below pay range",
  above: "Above pay range",
};

/** Map the frozen Fair Pay block (Rule B-20) to the drawer card's shape. */
export function mapFairPayAtEffectiveDate(block) {
  if (!block) return null;
  const status = FAIR_PAY_STATUS_LABELS[block.fair_pay_status] || "Fair Pay not compared";
  const readiness =
    block.readiness_pct != null
      ? ` at ${block.readiness_pct}% readiness`
      : "";
  const asOf = block.readiness_as_of ? ` on ${block.readiness_as_of}` : "";
  return {
    statusText: `${status}${readiness}${asOf}`,
    position: block.position?.name || block.position?.code || null,
    payRangeVersion: block.pay_range_version?.code || null,
  };
}

/** Map the detail payload to the presentational LapsedPayPlanDrawer props. */
export function mapVersionDetail(detail) {
  if (!detail) return { version: null, fairPayAtEffectiveDate: null };

  const currencyCode = detail.currency?.code || null;
  return {
    version: {
      code: detail.version_code || "",
      status: normalizeVersionStatus(detail.status),
      effective_from: detail.effective_from || null,
      effective_to: detail.effective_to || null,
      country: detail.country?.code || detail.country?.name || null,
      currency: currencyCode,
      currencySymbol: currencySymbolFor(currencyCode),
      amount: toNumber(detail.amount),
      payBasis: detail.pay_basis || null,
      fte: detail.allocation_fte ?? null,
    },
    fairPayAtEffectiveDate: mapFairPayAtEffectiveDate(detail.fair_pay_at_effective_date),
  };
}

/**
 * Wiring for the LapsedPayPlanDrawer (ER-12113).
 *
 * Loads a committed version's detail, classifies its lapsed reason, and runs
 * the Void action — all gated behind the Fair Pay MVP flag (idea-MDP-429).
 * A 409 response surfaces the server's own message inline; other failures fall
 * back to the proxy's notification service.
 */
export function useLapsedPayPlanDrawer({ proxy, fetchRows } = {}) {
  const state = reactive({
    open: false,
    loading: false,
    submitting: false,
    error: null,
    accountId: null,
    employeeId: null,
    versionId: null,
    detail: null,
    mutationEnabled: false,
    voidOpen: false,
    voidReason: "",
  });

  const mapped = computed(() => mapVersionDetail(state.detail));
  const version = computed(() => mapped.value.version);
  const fairPayAtEffectiveDate = computed(() => mapped.value.fairPayAtEffectiveDate);
  const lapsedReason = computed(() => resolveLapsedReason(state.detail));
  const voidable = computed(() => state.detail?.voidable === true);
  const voidBlockedBy = computed(() => state.detail?.void_blocked_by || null);
  const featureDisabled = computed(() => !state.mutationEnabled);

  function notify(type, message) {
    const notifier = proxy?.$notifications?.[type];
    if (typeof notifier === "function") {
      notifier(message);
    } else if (type === "error") {
      state.error = message;
    }
  }

  function setInlineError(error, fallback) {
    state.error = apiErrorMessage(error, fallback);
  }

  async function ensureMutationEnabled() {
    try {
      state.mutationEnabled =
        (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_429, false)) === true;
    } catch {
      state.mutationEnabled = false;
    }
    return state.mutationEnabled;
  }

  async function loadDetail(versionId) {
    if (!state.accountId || versionId == null) return;
    state.loading = true;
    state.error = null;
    try {
      const response = await getPayPlanVersionDetailV2(state.accountId, versionId);
      state.detail = response?.data || null;
    } catch (error) {
      state.detail = null;
      setInlineError(error, "Could not load the pay plan version");
    } finally {
      state.loading = false;
    }
  }

  function open({ accountId, employeeId, versionId } = {}) {
    state.open = true;
    state.error = null;
    state.accountId = accountId || getAccountId();
    state.employeeId = employeeId || null;
    state.versionId = versionId ?? null;
    state.detail = null;
    state.voidOpen = false;
    state.voidReason = "";
    ensureMutationEnabled();
    loadDetail(state.versionId);
  }

  function close() {
    state.open = false;
    state.error = null;
    state.detail = null;
    state.voidOpen = false;
    state.voidReason = "";
  }

  function openVoid() {
    state.voidReason = "";
    state.voidOpen = true;
  }

  function cancelVoid() {
    state.voidOpen = false;
    state.voidReason = "";
  }

  async function refresh() {
    if (fetchRows) await fetchRows();
  }

  async function voidVersion() {
    const reason = state.voidReason.trim();
    if (!reason) return null;

    if (!state.mutationEnabled && !(await ensureMutationEnabled())) {
      notify("error", "Fair Pay pay plan actions are not available");
      return null;
    }

    state.submitting = true;
    state.error = null;
    try {
      await voidPayPlanVersionV2(state.accountId, state.versionId, { reason });
      notify("success", "Pay plan version voided");
      state.voidOpen = false;
      close();
      await refresh();
      return true;
    } catch (error) {
      if (error?.response?.status === 409) {
        setInlineError(error, "Could not void the pay plan version");
      } else {
        setInlineError(error, "Could not void the pay plan version");
        notify("error", apiErrorMessage(error, "Could not void the pay plan version"));
      }
      return null;
    } finally {
      state.submitting = false;
    }
  }

  return {
    state,
    version,
    fairPayAtEffectiveDate,
    lapsedReason,
    voidable,
    voidBlockedBy,
    featureDisabled,
    open,
    close,
    openVoid,
    cancelVoid,
    voidVersion,
  };
}
